import { useShortcutHandler } from '@sunucupark/client-react';
import type { MailFolder, SystemFolderKind } from '@sunucupark/shared';
import { qk, useSession } from '@sunucupark/client-react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useState } from 'react';
import { CommandPaletteBridge } from './CommandPaletteBridge';
import { ShortcutHelp } from './ShortcutHelp';

/** The folders the `g` sequences jump to. */
const JUMP_TARGETS: Readonly<Record<string, SystemFolderKind>> = {
  'go.inbox': 'inbox',
  'go.sent': 'sent',
  'go.drafts': 'drafts',
};

/** Typing in a field is typing, not a shortcut. */
function isTyping(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
}

/**
 * The keyboard for the whole window: only the `global` scope lives here, so a
 * pane keeps its own keys on its own element and two listeners never race.
 * The registry decides what a key means (ADR 0006); this only listens.
 */
export function ShortcutBridge() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const session = useSession();
  const [helpOpen, setHelpOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const accountId = session?.accountId;

  const run = useCallback(
    (id: string) => {
      if (id === 'app.help') {
        setHelpOpen((open) => !open);
        return;
      }
      if (id === 'app.palette') {
        setPaletteOpen((open) => !open);
        return;
      }
      const kind = JUMP_TARGETS[id];
      if (!kind || !accountId) return;
      const folders = queryClient.getQueryData<MailFolder[]>(qk.folders(accountId));
      const folder = folders?.find((candidate) => candidate.systemKind === kind);
      if (folder) void navigate({ to: '/mail/$folderId', params: { folderId: folder.id }, search: { view: 'conversation' } });
    },
    [accountId, navigate, queryClient],
  );

  const handle = useShortcutHandler(['global'], run);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (isTyping(event.target) || event.defaultPrevented) return;
      const handled = handle({
        key: event.key,
        ctrl: event.ctrlKey,
        meta: event.metaKey,
        alt: event.altKey,
        shift: event.shiftKey,
      });
      if (handled) event.preventDefault();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [handle]);

  return (
    <>
      <ShortcutHelp open={helpOpen} onOpenChange={setHelpOpen} />
      <CommandPaletteBridge
        open={paletteOpen}
        onOpenChange={setPaletteOpen}
        onShowShortcuts={() => {
          setPaletteOpen(false);
          setHelpOpen(true);
        }}
      />
    </>
  );
}
